import { useState } from 'react';
import { Badge, fmtDate, ListPage, Modal, usePagedList } from '@greenrinse/ui';

interface AuditRow { id: string; atUtc: string; userId: string | null; userName: string | null; action: string; entityType: string; entityId: string | null; summary: string | null; changesJson: string | null; reason: string | null; ipAddress: string | null }

const ACTIONS = ['Create', 'Update', 'Delete', 'Login', 'LoginFailed', 'Logout', 'SettingChanged', 'Approve', 'Reject', 'Correct'];
type Tone = 'default' | 'success' | 'warning' | 'error' | 'accent';
function tone(a: string): Tone { return a === 'Delete' || a === 'Reject' || a === 'LoginFailed' ? 'error' : a === 'Create' || a === 'Approve' ? 'success' : a === 'SettingChanged' ? 'warning' : 'default'; }

export default function AuditPage() {
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const list = usePagedList<AuditRow>('/api/audit', { action: action || undefined, entityType: entityType || undefined }, 50);
  const [open, setOpen] = useState<AuditRow | null>(null);
  const pretty = (json: string | null) => { if (!json) return ''; try { return JSON.stringify(JSON.parse(json), null, 2); } catch { return json; } };

  return (
    <>
      <ListPage<AuditRow>
        title="Audit log" state={list} rowKey={(a) => a.id} onRowClick={(a) => setOpen(a)}
        filters={<>
          <select className="gr-select" style={{ width: 'auto', minHeight: 40 }} value={action} onChange={(e) => setAction(e.target.value)}><option value="">All actions</option>{ACTIONS.map((a) => <option key={a}>{a}</option>)}</select>
          <input className="gr-input" style={{ maxWidth: 200, minHeight: 40 }} placeholder="Entity type (e.g. Contract)" value={entityType} onChange={(e) => setEntityType(e.target.value)} />
        </>}
        columns={[
          { key: 'at', header: 'When', render: (a) => fmtDate(a.atUtc), width: 170 },
          { key: 'who', header: 'Who', render: (a) => a.userName ?? (a.userId ? a.userId.slice(0, 8) : 'system') },
          { key: 'action', header: 'Action', render: (a) => <Badge tone={tone(a.action)}>{a.action}</Badge> },
          { key: 'entity', header: 'Entity', render: (a) => <>{a.entityType}{a.entityId && <span className="gr-muted" style={{ fontSize: 12 }}> · {a.entityId.slice(0, 8)}</span>}</> },
          { key: 'summary', header: 'Summary', render: (a) => a.summary ?? '—' },
          { key: 'ip', header: 'IP', render: (a) => a.ipAddress ?? '—' },
        ]}
      />
      <Modal open={open !== null} title="Audit entry" onClose={() => setOpen(null)}>
        {open && (
          <div className="gr-stack">
            <dl className="gr-kv">
              <dt>When</dt><dd>{fmtDate(open.atUtc)}</dd>
              <dt>User</dt><dd>{open.userName ?? open.userId ?? 'system'}</dd>
              <dt>Action</dt><dd>{open.action}</dd>
              <dt>Entity</dt><dd>{open.entityType} {open.entityId}</dd>
              {open.summary && <><dt>Summary</dt><dd>{open.summary}</dd></>}
              {open.reason && <><dt>Reason</dt><dd>{open.reason}</dd></>}
            </dl>
            {open.changesJson && <pre className="gr-json">{pretty(open.changesJson)}</pre>}
          </div>
        )}
      </Modal>
    </>
  );
}
